import { useState } from 'react';
import { clampLayout, LAYOUT_LIMITS, type Layout, type LayoutHeightKey, type LayoutKey, type LayoutWidthKey } from '@jerome2/shared';
import { useStore } from '../lib/store.ts';
import { app, saveLayout } from './app.ts';
import { errorToast } from './toast.ts';

// Instelbare breedtes van de desktop (ticket 28376). De instelling `settings.layout` (shared/src/layout.ts) zegt hoe breed de gebruiker
// een onderdeel wil; hier staat hoe breed het bij de ruimte van dat moment wordt. Een onderdeel neemt nooit zoveel dat het paneel
// ernaast minder dan PANEL_MIN houdt: de gesprekkenkolom niet van kolom en panelen samen, de werkerskolom niet van de hele rij,
// het mailpaneel niet van het chatscherm. Is de ruimte daarvoor te klein, dan wint het minimum van het onderdeel. De instelling
// zelf verandert daarbij niet: wordt het venster weer breder, dan komt de ingestelde breedte terug. Tijdens het slepen geldt een
// eigen waarde (useLayout); loslaten of een toets bewaart hem met saveLayout.
// Sinds ticket 28625 ook verdelingen van een hoogte: de lijst boven de lezer op het tabblad Mail en de gevonden mails boven de
// mail in het mailpaneel van de chat; daar houden de lijst en de mail een minimum (SPLIT_MIN), op dezelfde manier.

/** Zoveel px houdt het paneel naast een instelbaar onderdeel minstens. */
export const PANEL_MIN = 480;

/** De grenzen van een breedte bij deze ruimte (het onderdeel samen met het paneel ernaast). */
export function widthBounds(key: LayoutWidthKey, space: number): { min: number; max: number } {
  const { min, max } = LAYOUT_LIMITS[key];
  return { min, max: Math.max(min, Math.min(max, Math.floor(space - PANEL_MIN))) };
}

/** De breedte die een onderdeel bij deze ruimte krijgt: de ingestelde, binnen widthBounds. */
export function fitWidth(key: LayoutWidthKey, value: number, space: number): number {
  const { min, max } = widthBounds(key, space);
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Een breedte voor een schermlezer (aria-valuetext van de greep). */
export const pixelsText = (value: number): string => `${Math.round(value)} pixels`;

/** Zoveel px houden de lijst (vóór de greep) en de mail (erna) minstens bij een verdeling van een hoogte (ticket 28625): de lijst
 * boven de lezer op het tabblad Mail, en de gevonden mails boven de mail in het mailpaneel van de chat. */
export const SPLIT_MIN: Record<LayoutHeightKey, { list: number; mail: number }> = {
  mailSplit: { list: 120, mail: 240 },
  mailPaneSplit: { list: 96, mail: 240 },
};

/** Op drie decimalen naar boven of naar beneden, zonder dat een rekenrestje een duizendste scheelt. */
const up = (v: number) => Math.ceil(v * 1000 - 1e-6) / 1000;
const down = (v: number) => Math.floor(v * 1000 + 1e-6) / 1000;

/** De grenzen van een verdeling van een hoogte bij deze hoogte in px (0: nog niet gemeten): binnen de grenzen van de instelling,
 * en zo dat de lijst en de mail hun minimum houden. Is de hoogte daarvoor te klein, dan wint het minimum van de lijst. */
export function splitBounds(key: LayoutHeightKey, space: number): { min: number; max: number } {
  const { min, max } = LAYOUT_LIMITS[key];
  if (!(space > 0)) return { min, max };
  const lo = up(Math.max(min, SPLIT_MIN[key].list / space));
  const hi = down(Math.min(max, 1 - SPLIT_MIN[key].mail / space));
  return { min: lo, max: Math.max(lo, hi) };
}

/** De verdeling die een hoogte bij deze ruimte krijgt: de ingestelde, binnen splitBounds, op drie decimalen. */
export function fitSplit(key: LayoutHeightKey, value: number, space: number): number {
  const { min, max } = splitBounds(key, space);
  return Math.min(max, Math.max(min, Math.round(value * 1000) / 1000));
}

/** Een verdeling voor een schermlezer (aria-valuetext van een liggende greep), zoals "lijst 40 procent". */
export const percentText =
  (part: string) =>
  (value: number): string =>
    `${part} ${Math.round(value)} procent`;

export interface LayoutValue {
  /** De waarde van nu: tijdens het slepen de eigen, anders de bewaarde. */
  value: number;
  /** Tijdens het slepen (of een pijltje): alleen in beeld. */
  change: (value: number) => void;
  /** Losgelaten, een toets of dubbelklik: bewaren (meteen in beeld, dan naar de server). */
  commit: (value: number) => void;
}

/** Eén instelbare breedte (of de verdeling tussen de panelen) voor een greep (ui/Resizer.tsx). */
export function useLayout(key: LayoutKey): LayoutValue {
  const stored = useStore(app, (s) => s.settings.layout[key]);
  const [live, setLive] = useState<number | null>(null);
  return {
    value: live ?? stored,
    change: (value) => setLive(clampLayout(key, value)),
    commit: (value) => {
      setLive(null);
      const patch: Partial<Layout> = {};
      patch[key] = clampLayout(key, value);
      void saveLayout(patch).catch((e) => errorToast(e, 'Breedte niet bewaard'));
    },
  };
}
